import {
    ATTRIBUTION_TAB_IDS,
    CAMPAIGN_OBJECTIVE_TO_POSTBACK_EVENTS,
    EVENT_MAPPING_EVENT_TYPES,
    POSTBACK_EVENT_TYPE_OPTIONS,
    getDefaultPostbackMacros,
    isPostbackMacroDisallowedForCell,
    urlField,
} from './consts';
import { resolveTemplateEntries } from './templateEntries';

const POSTBACK_TAB = ATTRIBUTION_TAB_IDS.POSTBACK_OVERALL_SETUP;
const TEMPLATES_TAB = ATTRIBUTION_TAB_IDS.POSTBACK_TEMPLATES;

export function activeEventTypes(entries) {
    return new Set(entries.filter(e => (e.platforms || []).length > 0).map(e => e.event_type));
}

export function collectCombos(entries) {
    const set = new Set();
    entries.forEach(e => (e.platforms || []).forEach(p => set.add(`${e.event_type}|${p}`)));
    return set;
}

export function removedCombos(before, after) {
    const beforeS = collectCombos(before);
    const afterS = collectCombos(after);
    return [...beforeS]
        .filter(k => !afterS.has(k))
        .map(k => {
            const [eventType, platform] = k.split('|');
            return { eventType, platform };
        });
}

export function addedCombos(before, after) {
    const beforeS = collectCombos(before);
    const afterS = collectCombos(after);
    return [...afterS]
        .filter(k => !beforeS.has(k))
        .map(k => {
            const [eventType, platform] = k.split('|');
            return { eventType, platform };
        });
}

export function stripTemplatesFor(templates, combos) {
    if (!templates || combos.length === 0) return templates;
    const result = { ...templates };
    combos.forEach(({ eventType, platform }) => {
        if (!result[eventType]) return;
        const eventBucket = { ...result[eventType] };
        delete eventBucket[platform];
        if (Object.keys(eventBucket).length === 0) {
            delete result[eventType];
        } else {
            result[eventType] = eventBucket;
        }
    });
    return result;
}

export function hasEventMappingData(tabData) {
    return (
        !!tabData.enable_event_mapping ||
        !!tabData.event_parameter_key_name ||
        (tabData.event_names || []).some(n => !!n)
    );
}

export function hasActiveMappingType(entries) {
    return entries.some(e => EVENT_MAPPING_EVENT_TYPES.includes(e.event_type) && (e.platforms || []).length > 0);
}

export function removedEventTypesWithUrl(eventsAndPlatforms, next, tabData) {
    const before = activeEventTypes(eventsAndPlatforms);
    const after = activeEventTypes(next);
    return [...before].filter(et => !after.has(et) && !!tabData[urlField(et)]?.trim());
}

export function templateHasFilledRows(templatesFormData, eventType, platform) {
    const rows = templatesFormData?.[eventType]?.[platform] || [];
    return rows.some(r => !!r.partner_parameter_key?.trim());
}

export function removedCombosWithFilledTemplate(before, after, templatesFormData) {
    return removedCombos(before, after).filter(({ eventType, platform }) =>
        templateHasFilledRows(templatesFormData, eventType, platform)
    );
}

/**
 * Unchecks one event/platform row in Postback Overall Setup (same shape as table toggle).
 * @param {object[]} entries current `postback_events_and_platforms`
 * @param {string} eventType event type key
 * @param {string} platform platform key
 * @returns {object[]} next `postback_events_and_platforms` entries
 */
export function buildNextEntriesAfterRemovingCombo(entries, eventType, platform) {
    const checkedMap = {};
    entries.forEach(entry => {
        checkedMap[entry.event_type] = new Set(entry.platforms || []);
    });
    const currentSet = new Set(checkedMap[eventType] || []);
    currentSet.delete(platform);
    return POSTBACK_EVENT_TYPE_OPTIONS.map(et => ({
        event_type: et,
        platforms: [...(et === eventType ? currentSet : checkedMap[et] || new Set())],
    })).filter(item => item.platforms.length > 0);
}

/**
 * Same side-effect ordering as PostbackEventsPlatformsTable `applyNext`: URLs, different domains,
 * event mapping, template rows stripped for removed combos, and default macro rows for newly added combos.
 * @param {object} prevFormData full integration form `formData` snapshot before the toggle
 * @param {object[]} next postback_events_and_platforms entries after toggle
 * @param {string|null|undefined} postbackMacroNetworkName partner macro network token for default rows
 * @returns {object} next full `formData` snapshot
 */
export function applyPostbackEventsPlatformsNext(prevFormData, next, postbackMacroNetworkName) {
    const prevTab = prevFormData[POSTBACK_TAB] || {};
    const eventsAndPlatforms = prevTab.postback_events_and_platforms || [];
    const after = activeEventTypes(next);
    // Derive the disallow predicate from the active platform_template so newly-seeded cells
    // never plant macros the BE schema would reject (e.g. {EVTNAME} on HasOffers conversions).
    const platformTemplate = prevFormData[ATTRIBUTION_TAB_IDS.GENERAL]?.platform_template;
    const nextTab = { ...prevTab, postback_events_and_platforms: next };
    POSTBACK_EVENT_TYPE_OPTIONS.forEach(et => {
        if (prevTab[urlField(et)] !== undefined && !after.has(et)) {
            nextTab[urlField(et)] = undefined;
        }
    });
    if (prevTab.different_domains_per_postback && next.length === 0) {
        nextTab.different_domains_per_postback = false;
    }
    const stillHasMappingType = [...after].some(et => EVENT_MAPPING_EVENT_TYPES.includes(et));
    if (!stillHasMappingType) {
        if (prevTab.enable_event_mapping) nextTab.enable_event_mapping = false;
        if (prevTab.event_parameter_key_name !== undefined) nextTab.event_parameter_key_name = undefined;
        if (prevTab.event_names !== undefined) nextTab.event_names = undefined;
    }
    const nextFormData = { ...prevFormData, [POSTBACK_TAB]: nextTab };
    const prevTemplates = prevFormData[TEMPLATES_TAB];
    const combosToStrip = removedCombos(eventsAndPlatforms, next);
    const combosToAdd = addedCombos(eventsAndPlatforms, next);
    if (combosToStrip.length || combosToAdd.length) {
        let nextTemplates = prevTemplates;
        if (prevTemplates && combosToStrip.length) {
            nextTemplates = stripTemplatesFor(prevTemplates, combosToStrip);
        }
        if (combosToAdd.length) {
            let seeded = nextTemplates || {};
            combosToAdd.forEach(({ eventType, platform }) => {
                // For affiliate templates, seed only the macros the template auto-fills (with
                // their keys pre-populated). The all-defaults seeding used to plant a dozen empty
                // rows the user then had to ignore — matches the cascade's drop-empties filter.
                // 'none' (or no template selected) keeps the legacy all-defaults seeding so the
                // user can map their custom URL however they want.
                const templateEntries = platformTemplate
                    ? resolveTemplateEntries(platformTemplate, eventType, platform, postbackMacroNetworkName)
                    : [];
                let rows;
                if (templateEntries.length) {
                    rows = templateEntries.map(e => ({
                        singular_macro: e.resolvedMacro,
                        partner_parameter_key: e.key,
                    }));
                } else {
                    // Skip seeding macros forbidden in this (template, event_type) cell so the
                    // cascade never plants data the BE schema would reject.
                    const defaults = getDefaultPostbackMacros(platform, eventType, postbackMacroNetworkName).filter(
                        macro => !isPostbackMacroDisallowedForCell(platformTemplate, eventType, macro)
                    );
                    rows = defaults.map(macro => ({ singular_macro: macro, partner_parameter_key: '' }));
                }
                seeded = {
                    ...seeded,
                    [eventType]: { ...(seeded[eventType] || {}), [platform]: rows },
                };
            });
            nextTemplates = seeded;
        }
        nextFormData[TEMPLATES_TAB] = nextTemplates;
    }
    return nextFormData;
}

/**
 * Mirrors checkbox-deselect warning reasons in PostbackEventsPlatformsTable.
 * @param {object} options argument bag for the deselect preview
 * @param {object} options.tabData postback overall setup tab values
 * @param {object[]} options.eventsAndPlatforms current postback_events_and_platforms
 * @param {object[]} options.next postback_events_and_platforms after the toggle
 * @param {object} [options.templatesFormData] platform_templates tab object
 * @param {string} options.toggledEventType event type row being toggled
 * @returns {string[]} issue keys for confirm dialog (may be empty)
 */
export function getDeselectIssues({ tabData, eventsAndPlatforms, next, templatesFormData, toggledEventType }) {
    const wouldRemoveEventMapping =
        EVENT_MAPPING_EVENT_TYPES.includes(toggledEventType) &&
        hasActiveMappingType(eventsAndPlatforms) &&
        !hasActiveMappingType(next) &&
        hasEventMappingData(tabData);
    const wouldClearUrls = removedEventTypesWithUrl(eventsAndPlatforms, next, tabData).length > 0;
    const wouldForceOffDifferentDomains =
        !!tabData.different_domains_per_postback && eventsAndPlatforms.length > 0 && next.length === 0;
    const wouldDeleteFilledTemplate =
        removedCombosWithFilledTemplate(eventsAndPlatforms, next, templatesFormData).length > 0;

    const issues = [];
    if (wouldDeleteFilledTemplate) issues.push('DESELECT_CLEAR_TEMPLATE');
    if (wouldForceOffDifferentDomains) issues.push('DESELECT_DISABLE_DIFFERENT_DOMAINS');
    else if (wouldClearUrls) issues.push('DESELECT_CLEAR_URLS');
    if (wouldRemoveEventMapping) issues.push('DESELECT_CLEAR_EVENT_MAPPING');
    return issues;
}

/**
 * UI-only convenience: when the user toggles a campaign objective ON in the General tab, append
 * any mapped postback event-type rows (with current GENERAL platforms) to
 * `postback_events_and_platforms`. Returns the original formData reference if any guard fails so
 * Formik can shallow-bail on no-op updates.
 *
 * Runtime guards checked inside this function (all must hold):
 *   1. The objective is a key in CAMPAIGN_OBJECTIVE_TO_POSTBACK_EVENTS.
 *   2. GENERAL.receive_postbacks === true.
 *   3. GENERAL.platforms is non-empty.
 *   4. None of the mapped event types are already present in postback_events_and_platforms.
 *
 * Caller precondition (NOT checked here): this function must only be invoked on toggle-ON;
 * toggle-OFF must not call this helper.
 *
 * @param {object} formData full integration form `formData` snapshot (post-objective-update)
 * @param {string} objective campaign objective just toggled ON
 * @param {string|null|undefined} postbackMacroNetworkName partner macro network token, forwarded
 *     to `applyPostbackEventsPlatformsNext` for default macro seeding
 * @returns {object} next full `formData` snapshot, or the original `formData` reference on no-op
 */
export function applyObjectivePostbackDefaults(formData, objective, postbackMacroNetworkName) {
    const mappedEventTypes = CAMPAIGN_OBJECTIVE_TO_POSTBACK_EVENTS[objective];
    if (!mappedEventTypes || mappedEventTypes.length === 0) return formData;

    const general = formData[ATTRIBUTION_TAB_IDS.GENERAL] || {};
    if (general.receive_postbacks !== true) return formData;

    const platforms = general.platforms || [];
    if (platforms.length === 0) return formData;

    const postbackTab = formData[POSTBACK_TAB] || {};
    const eventsAndPlatforms = postbackTab.postback_events_and_platforms || [];
    const existingEventTypes = new Set(eventsAndPlatforms.map(e => e.event_type));

    const eventTypesToAdd = mappedEventTypes.filter(et => !existingEventTypes.has(et));
    if (eventTypesToAdd.length === 0) return formData;

    const next = [...eventsAndPlatforms, ...eventTypesToAdd.map(et => ({ event_type: et, platforms: [...platforms] }))];

    return applyPostbackEventsPlatformsNext(formData, next, postbackMacroNetworkName);
}
